export interface QualityCheck {
  id: string;
  category: string;
  status: 'pass' | 'warning' | 'fail' | 'skipped';
  severity: 'error' | 'warning' | 'info';
  evidence: Record<string, string | number | boolean | null>;
}

export interface QualityReport {
  version: string;
  status: 'passed' | 'warning' | 'failed' | 'attention';
  score: number;
  summary: { passed: number; warnings: number; failed: number; skipped: number };
  category_scores?: Record<string, number>;
  metrics: Record<string, unknown>;
  checks: QualityCheck[];
  scoring?: { version: string; category_weights: Record<string, number>; status_values: Record<string, number>; score_cap_metric?: string };
}

export interface MetadataReadinessInput {
  title?: string;
  description?: string;
  keywords?: string[];
  category?: string;
  marketplace?: 'adobe' | 'shutterstock' | 'freepik' | 'dreamstime' | 'depositphotos';
  validation_status?: 'ready' | 'warning' | 'blocked' | 'draft';
  validation?: { status?: string; errors?: string[]; warnings?: string[]; keywords?: string[] };
}

const CATEGORY_WEIGHTS: Record<string, number> = { vector_integrity: 35, export_compliance: 25, visual_quality: 25, complexity: 10, metadata_readiness: 5 };
const STATUS_VALUES: Record<string, number> = { pass: 1, warning: 0.6, fail: 0 };
export const MARKETPLACE_QUALITY_PROFILES = {
  adobe: { name: 'Adobe Stock', title: 70, keywords: 49, minimumKeywords: 5, descriptionRequired: false, note: 'Title and keyword limits follow workspace marketplace validation. Category remains a Vectria readiness recommendation.' },
  shutterstock: { name: 'Shutterstock', title: 200, keywords: 50, minimumKeywords: 5, descriptionRequired: true, note: 'Description and metadata limits follow workspace marketplace validation. Quality score remains a Vectria estimate.' },
  freepik: { name: 'Freepik Contributor', title: 100, keywords: 50, minimumKeywords: 5, descriptionRequired: true, note: 'Metadata limits follow workspace marketplace validation. Quality score remains a Vectria estimate.' },
  dreamstime: { name: 'Dreamstime', title: 250, keywords: 50, minimumKeywords: 7, descriptionRequired: true, note: 'Description and keyword rules follow workspace marketplace validation. Quality score remains a Vectria estimate.' },
  depositphotos: { name: 'Depositphotos', title: 200, keywords: 50, minimumKeywords: 5, descriptionRequired: true, note: 'Metadata limits follow workspace validation; IPTC/XMP guidance and quality score are advisory.' },
} as const;
export type MetadataMarketplace = keyof typeof MARKETPLACE_QUALITY_PROFILES;

export function recomputeQualityScore(report: QualityReport): QualityReport {
  const categoryScores: Record<string, number> = {};
  let weightedTotal = 0;
  let activeWeight = 0;
  const categoryWeights = report.scoring?.category_weights || CATEGORY_WEIGHTS;
  const statusValues = report.scoring?.status_values || STATUS_VALUES;
  for (const [category, weight] of Object.entries(categoryWeights)) {
    const scored = report.checks.filter(check => check.category === category && check.status in statusValues);
    if (!scored.length) continue;
    const value = scored.reduce((sum, check) => sum + statusValues[check.status], 0) / scored.length;
    categoryScores[category] = Math.round(value * 100);
    weightedTotal += value * weight;
    activeWeight += weight;
  }
  const summary = {
    passed: report.checks.filter(check => check.status === 'pass').length,
    warnings: report.checks.filter(check => check.status === 'warning').length,
    failed: report.checks.filter(check => check.status === 'fail').length,
    skipped: report.checks.filter(check => check.status === 'skipped').length,
  };
  let score = activeWeight ? Math.round(weightedTotal * 100 / activeWeight) : 0;
  const capMetric = report.scoring?.score_cap_metric || 'quality_gate_score';
  const capValue = report.metrics?.[capMetric];
  if (typeof capValue === 'number' && Number.isFinite(capValue)) {
    score = Math.min(score, Math.max(0, Math.min(100, Math.round(capValue))));
  }
  return { ...report, score, category_scores: categoryScores, summary, status: summary.failed ? 'failed' : summary.warnings ? 'warning' : 'passed' };
}

export function applyMetadataReadiness(report: QualityReport, metadata?: MetadataReadinessInput): QualityReport {
  const retained = report.checks.filter(check => check.category !== 'metadata_readiness');
  if (!metadata) return recomputeQualityScore({ ...report, checks: [...retained, { id: 'metadata_readiness', category: 'metadata_readiness', status: 'skipped', severity: 'info', evidence: {} }] });

  const marketplace = metadata.marketplace || 'adobe';
  const limits = MARKETPLACE_QUALITY_PROFILES[marketplace];
  const title = String(metadata.title || '').trim();
  const description = String(metadata.description || '').trim();
  const keywords = Array.isArray(metadata.keywords) ? metadata.keywords.map(value => String(value).trim()).filter(Boolean) : [];
  const normalized = keywords.map(value => value.toLocaleLowerCase());
  const duplicateCount = normalized.length - new Set(normalized).size;
  const validationStatus = metadata.validation?.status || metadata.validation_status || 'draft';
  const validationIssueCount = (metadata.validation?.errors?.length || 0) + (metadata.validation?.warnings?.length || 0);
  const checks: QualityCheck[] = [
    { id: 'metadata_title_present', category: 'metadata_readiness', status: title.length >= 5 ? 'pass' : 'fail', severity: 'error', evidence: { actual: title.length, expected: '5+ characters' } },
    { id: 'metadata_title_length', category: 'metadata_readiness', status: title.length <= limits.title ? 'pass' : 'fail', severity: 'error', evidence: { actual: title.length, expected: `5–${limits.title} characters`, marketplace } },
    { id: 'metadata_description_present', category: 'metadata_readiness', status: !limits.descriptionRequired || Boolean(description) ? 'pass' : 'fail', severity: 'error', evidence: { actual: description.length, expected: limits.descriptionRequired ? 'Required' : 'Optional', marketplace } },
    { id: 'metadata_keyword_count', category: 'metadata_readiness', status: keywords.length >= limits.minimumKeywords && keywords.length <= limits.keywords ? 'pass' : 'fail', severity: 'error', evidence: { actual: keywords.length, expected: `${limits.minimumKeywords}–${limits.keywords}`, marketplace } },
    { id: 'metadata_keyword_duplicates', category: 'metadata_readiness', status: duplicateCount ? 'warning' : 'pass', severity: 'warning', evidence: { actual: duplicateCount, expected: 0 } },
    { id: 'metadata_category_present', category: 'metadata_readiness', status: metadata.category?.trim() ? 'pass' : 'warning', severity: 'warning', evidence: { actual: Boolean(metadata.category?.trim()), expected: true } },
    { id: 'metadata_validation', category: 'metadata_readiness', status: validationStatus === 'blocked' ? 'fail' : validationStatus === 'ready' ? 'pass' : 'warning', severity: validationStatus === 'blocked' ? 'error' : 'warning', evidence: { actual: validationStatus, expected: 'ready', issue_count: validationIssueCount } },
  ];
  return recomputeQualityScore({ ...report, metrics: { ...report.metrics, metadata_marketplace_profile: limits.name, metadata_profile_basis: 'Marketplace validation + Vectria advisory checks', metadata_title_length: title.length, metadata_description_length: description.length, metadata_keyword_count: keywords.length, metadata_duplicate_keyword_count: duplicateCount }, checks: [...retained, ...checks] });
}
